/**
 * First-store provisioning.
 *
 * Ports BigxBookApp/src/screens/setup/StoreSetupScreen.tsx's `submitSetup()`,
 * with one structural change forced by the web platform.
 *
 * ── Why this cannot be a byte-copy of Mobile ─────────────────────────────────
 * Mobile and Desktop have ONE database, so they create the store row first and
 * read its generated id afterwards. Web keeps one IndexedDB per store —
 * `bigxbook-web-<storeId>` (see db/index.ts) — so the id has to exist *before*
 * there is a database to create the row in. Chicken and egg.
 *
 * Resolved by minting the id on the client: `crypto.randomUUID()`. That is the
 * right shape rather than a convenient one — `Store.id` is `@default(uuid())`
 * in prisma/schema.prisma, so a client-generated UUID is indistinguishable from
 * a server-generated one, and WatermelonDB's push sends created rows with their
 * local ids anyway. Mobile already does exactly this for the merchant row,
 * which reuses the server-assigned `merchantId`.
 *
 * ── Ordering ────────────────────────────────────────────────────────────────
 * The store is created LOCALLY and reaches the server through the normal
 * `/api/sync/push`, exactly as on the other two clients. There is no
 * "create store" REST endpoint and this deliberately does not add one:
 * a till that can only open a store while online would be a worse till.
 */
import { Q } from '@nozbe/watermelondb';
import { activateStore, getActiveDatabase } from '@/db';
import { useAppStore } from '@/store/useAppStore';
import { DEFAULT_SETTINGS } from '@/store/useSettingsStore';
import type Merchant from '@/db/models/Merchant';
import type Store from '@/db/models/Store';
import type User from '@/db/models/User';
import type { Setting } from '@/db/models/System';

export interface StoreSetupInput {
  name: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  phone?: string;
  gstin?: string;
  invoicePrefix?: string;
  /** Billing convention: are catalogue prices GST-inclusive? */
  taxInclusive: boolean;
}

/** Trim to null, so optional columns hold null rather than empty strings. */
function orNull(v: string | undefined): string | null {
  const t = v?.trim();
  return t ? t : null;
}

/**
 * Creates the merchant, store, user and default settings rows for a freshly
 * approved merchant, activates the new store, and returns its id.
 *
 * Idempotent per store: re-running against an already-provisioned database
 * updates rather than duplicating, so a half-finished setup (tab closed between
 * the write and the sync) can be retried without producing two stores.
 */
export async function provisionStore(input: StoreSetupInput): Promise<string> {
  const { currentUser, setActiveStore } = useAppStore.getState();
  if (!currentUser) throw new Error('Not signed in.');
  if (!input.name.trim()) throw new Error('Store name is required.');

  const storeId = crypto.randomUUID();

  // Open (and create) this store's IndexedDB before anything touches the DB.
  // `setActiveStore` also persists the id, so a reload mid-setup comes back to
  // the same database rather than stranding these rows in an orphaned one.
  activateStore(storeId);
  await setActiveStore(storeId);

  // Capture the concrete Database once. Re-reading the `database` proxy inside
  // a writer is unsafe — see getActiveDatabase() in db/index.ts.
  const db = getActiveDatabase();

  await db.write(async () => {
    // 1. Merchant — reuses the SERVER-assigned id, so the row the sync pushes
    //    merges into the merchant the backend already created at verify-otp
    //    rather than inserting a second one.
    //    The plan fields below deliberately mirror the values the backend
    //    itself writes when it creates the merchant at verify-otp (FREE, one
    //    store, three users). `merchants` is push-mutable and the server
    //    upserts by id, so this row will be written back over the server's —
    //    setting anything more generous here would hand out a plan nobody
    //    granted, and leaving them unset would push zeroes over real values.
    //    The first successful pull replaces all of it with server truth; these
    //    only govern the window before that, where `usePlanGate` would
    //    otherwise read an empty row.
    const merchant = await db.get<Merchant>('merchants').create((m) => {
      m._raw.id = currentUser.merchantId;
      m.businessName = input.name.trim();
      m.phone = currentUser.phone;
      m.onboardingStatus = 'APPROVED';
      m.planTier = 'FREE';
      m.subscriptionPlan = 'FREE';
      m.planStatus = 'ACTIVE';
      m.maxStores = 1;
      m.maxUsers = 3;
      // Must match the server's schema default. An unset WatermelonDB number
      // column is 0, not absent, so omitting this pushed 0 over the server's 1
      // — and `activeDeviceCount >= maxDevices` is true at 0 devices, so the
      // merchant could never register one once the limit is enforced. The
      // server now ignores these fields on push, but a local 0 would still be
      // read by usePlanGate in the window before the first pull.
      m.maxDevices = 1;
      m.isActive = true;
      m.gstin = orNull(input.gstin);
      m.state = orNull(input.state);
    });

    // 2. Store — the id minted above, so the row matches its own IndexedDB.
    await db.get<Store>('stores').create((s) => {
      s._raw.id = storeId;
      s.merchant.set(merchant);
      s.name = input.name.trim();
      s.address = orNull(input.address);
      s.city = orNull(input.city);
      s.state = orNull(input.state);
      s.pincode = orNull(input.pincode);
      s.phone = orNull(input.phone) ?? currentUser.phone;
      s.gstinOverride = orNull(input.gstin);
      s.currency = 'INR';
      s.invoicePrefix = input.invoicePrefix?.trim() || 'INV-';
      s.status = 'ACTIVE';
      s.isActive = true;
    });

    // 3. The signed-in user, as a local row. Billing, audit logs and RBAC all
    //    resolve the operator through this table, so without it the first sale
    //    has no author.
    try {
      const existing = await db.get<User>('users').find(currentUser.id);
      await existing.update((u) => {
        u.onboardingStatus = 'APPROVED';
        u.isSynced = false;
        u.version += 1;
      });
    } catch {
      await db.get<User>('users').create((u) => {
        u._raw.id = currentUser.id;
        u.merchant.set(merchant);
        u.phone = currentUser.phone;
        u.name = currentUser.name || 'Owner';
        u.role = currentUser.role || 'OWNER';
        u.onboardingStatus = 'APPROVED';
        u.isActive = true;
      });
    }

    // 4. Default settings. INVOICE_PREFIX is skipped because the store row is
    //    the authority for it, and TAX_INCLUSIVE takes the operator's answer —
    //    it decides whether catalogue prices already contain GST, so guessing
    //    it wrong silently misprices every subsequent sale.
    const settings = db.get<Setting>('settings');
    for (const [key, fallback] of Object.entries(DEFAULT_SETTINGS)) {
      if (key === 'INVOICE_PREFIX') continue;
      const value = key === 'TAX_INCLUSIVE' ? String(input.taxInclusive) : fallback;

      const already = await settings
        .query(Q.where('setting_key', key), Q.where('is_deleted', false))
        .fetchCount();
      if (already > 0) continue;

      await settings.create((row) => {
        row.merchant.set(merchant);
        row.storeId = storeId;
        row.category = 'N/A';
        row.settingKey = key;
        row.settingValue = value;
      });
    }
  });

  return storeId;
}
